import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient, { handleApiError } from "@/services/api/client";
import { ApiResponse } from "@/types/api.types";

// Types
export type PlanTier = "STARTER" | "TRADER" | "PRO";

export interface Plan {
  name: string;
  tier: PlanTier;
  price: number;
  features: string[];
  limits: {
    watchlistLimit: number;
    alertConfigLimit: number;
  };
}

export interface Subscription {
  status: "active" | "canceled" | "expired" | "none";
  plan: PlanTier;
  currentPeriodEnd?: string;
  limits: {
    watchlistLimit: number;
    alertConfigLimit: number;
  };
  usage?: {
    watchlistCount: number;
    alertConfigCount: number;
  };
}

export interface SubscriptionState {
  currentSubscription: Subscription | null;
  plans: Plan[];
  loading: boolean;
  error: string | null;
  checkoutUrl: string | null;
}

const initialState: SubscriptionState = {
  currentSubscription: null,
  plans: [],
  loading: false,
  error: null,
  checkoutUrl: null,
};

// API
export const subscriptionApi = {
  getPlans: async (): Promise<Plan[]> => {
    try {
      const response = await apiClient.get<ApiResponse<{ plans: Plan[] }>>(
        "/subscription/plans",
      );
      return response.data.data.plans;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  getSubscription: async (): Promise<Subscription> => {
    try {
      const response =
        await apiClient.get<ApiResponse<{ subscription: Subscription }>>(
          "/subscription",
        );
      return response.data.data.subscription;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  upgradePlan: async (plan: PlanTier): Promise<Subscription> => {
    try {
      const response = await apiClient.post<
        ApiResponse<{ subscription: Subscription }>
      >("/subscription/upgrade", { plan });
      return response.data.data.subscription;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  cancelSubscription: async (): Promise<void> => {
    try {
      await apiClient.post("/subscription/cancel");
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  createCheckoutSession: async (): Promise<string> => {
    try {
      const response = await apiClient.post<ApiResponse<{ url: string }>>(
        "/subscription/checkout",
      );
      return response.data.data.url;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};

// Async Thunks
export const fetchPlans = createAsyncThunk(
  "subscription/fetchPlans",
  async (_, { rejectWithValue }) => {
    try {
      const plans = await subscriptionApi.getPlans();
      return plans;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch plans");
    }
  },
);

export const fetchSubscription = createAsyncThunk(
  "subscription/fetchSubscription",
  async (_, { rejectWithValue }) => {
    try {
      const sub = await subscriptionApi.getSubscription();
      return sub;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch subscription");
    }
  },
);

export const createCheckoutSession = createAsyncThunk(
  "subscription/createCheckout",
  async (_, { rejectWithValue }) => {
    try {
      const url = await subscriptionApi.createCheckoutSession();
      return url;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to create checkout session");
    }
  },
);

export const cancelSubscription = createAsyncThunk(
  "subscription/cancel",
  async (_, { rejectWithValue }) => {
    try {
      await subscriptionApi.cancelSubscription();
      return null;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to cancel subscription");
    }
  },
);

// Slice
const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCheckoutUrl: (state) => {
      state.checkoutUrl = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Plans
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
        state.error = null;
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Subscription
    builder
      .addCase(fetchSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSubscription = action.payload;
        state.error = null;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create Checkout
    builder
      .addCase(createCheckoutSession.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.checkoutUrl = null;
      })
      .addCase(createCheckoutSession.fulfilled, (state, action) => {
        state.loading = false;
        state.checkoutUrl = action.payload;
        state.error = null;
      })
      .addCase(createCheckoutSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Cancel Subscription
    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state) => {
        state.loading = false;
        // Optimistically update status or re-fetch?
        // Usually fetching sub again is safer, but for now:
        if (state.currentSubscription) {
          state.currentSubscription.status = "canceled";
        }
        state.error = null;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCheckoutUrl } = subscriptionSlice.actions;

export const selectPlans = (state: { subscription: SubscriptionState }) =>
  state.subscription.plans;
export const selectSubscription = (state: {
  subscription: SubscriptionState;
}) => state.subscription.currentSubscription;
export const selectCheckoutUrl = (state: { subscription: SubscriptionState }) =>
  state.subscription.checkoutUrl;
export const selectSubscriptionLoading = (state: {
  subscription: SubscriptionState;
}) => state.subscription.loading;

export default subscriptionSlice.reducer;
